import { useEffect, useState } from "react";

import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import { misc, primary } from "app/themes/colors";

import { WHITE_SOFT } from "./dialstack-softphone-widget.styles";

export const FACT_STEP_MS = 2500;

// One line of the folded call bar, stepping through `facts` one at a time
// (ENG-2226). Each fact is held still for FACT_STEP_MS so it is read in one
// glance; a crawling ticker was tried in the mockups and read letter by
// letter. The step index is kept by position, not by text, so a live timer
// changing the first fact every second does not restart the cycle.
//
// `paused` (the bar is hovered or focused) freezes the line; reduced motion
// shows the first fact and nothing moves. The full list is the title, so
// nothing is lost because it stepped past.
export default function BarFactLine({
  facts,
  paused = false,
  tone = "default"
}: {
  facts: string[];
  paused?: boolean;
  // "live" tints the line mint (a call that is up), "held" amber.
  tone?: "default" | "live" | "held";
}) {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [index, setIndex] = useState(0);
  const stepping = facts.length > 1 && !paused && !reducedMotion;

  useEffect(() => {
    if (!stepping) return undefined;
    const timer = window.setInterval(
      () => setIndex((current) => current + 1),
      FACT_STEP_MS
    );
    return () => window.clearInterval(timer);
  }, [stepping]);

  const shown = facts.length === 0 ? "" : facts[index % facts.length];
  const color =
    tone === "live"
      ? primary[100]
      : tone === "held"
        ? misc.lightYellow
        : WHITE_SOFT;

  return (
    <Typography
      component="span"
      data-testid="call-bar-fact"
      title={facts.join(" · ")}
      sx={{
        display: "block",
        fontSize: 12,
        lineHeight: 1.25,
        color,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontVariantNumeric: "tabular-nums"
      }}
    >
      {shown}
    </Typography>
  );
}
